import { lazy, Suspense, useState } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { invoicesApi } from '../services/api';
import { useSuppliers } from '../hooks/useSuppliers';
import { useReferenceData } from '../hooks/useReferenceData';
import { StatusBadge } from '../components/shared/StatusBadge';
import { ValidationForm } from '../components/invoices/ValidationForm';
import { ArchiveStatusPanel } from '../components/invoices/ArchiveStatusPanel';
import { useToast } from '../context/ToastContext';

// react-pdf + pdfjs-dist pèsent ~430 KB. On charge le viewer à la demande
// (au montage de cette page) pour garder le bundle principal léger.
const PDFViewer = lazy(() =>
  import('../components/invoices/PDFViewer').then((m) => ({ default: m.PDFViewer })),
);

export function InvoiceValidationPage() {
  const { id = '' } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  // Préserve l'état complet de la liste (filtres + tri + recherche) au retour.
  // ?return=<encoded querystring> > ?from=<status> (rétrocompat).
  const returnQs = searchParams.get('return');
  const fromFilter = searchParams.get('from');
  const backToList = returnQs
    ? `/factures/liste?${decodeURIComponent(returnQs)}`
    : fromFilter
      ? `/factures/liste?status=${fromFilter}`
      : '/factures/liste';
  const invoiceQ = useQuery({
    queryKey: ['invoice', id],
    queryFn: () => invoicesApi.get(id),
    enabled: id.length > 0,
    // Pendant l'extraction Claude (status='pending'), on rafraîchit toutes les
    // 2 s pour que le formulaire se remplisse dès la fin (typiquement 10–20 s
    // après l'upload). Désactivé une fois sorti de 'pending'.
    refetchInterval: (query) => {
      const status = query.state.data?.invoice.status;
      return status === 'pending' ? 2000 : false;
    },
  });
  const suppliersQ = useSuppliers();
  const refQ = useReferenceData();

  const qc = useQueryClient();
  const toast = useToast();
  const [confirmeRelance, setConfirmeRelance] = useState(false);
  /**
   * Relancer la lecture du justificatif par Claude.
   *
   * Utile quand les règles d'extraction ont changé (une TVA étrangère qui doit
   * passer en TTC, une TVA à zéro qui n'en est pas une) ou quand la lecture
   * s'est trompée. L'extraction REMPLACE ce que Claude avait lu : une saisie en
   * cours qui n'a pas été enregistrée est perdue, d'où la confirmation.
   */
  const relancer = useMutation({
    mutationFn: () => invoicesApi.extract(id),
    onSuccess: () => {
      setConfirmeRelance(false);
      toast.success('Extraction relancée. Le formulaire se remplira dans quelques secondes.');
      void qc.invalidateQueries({ queryKey: ['invoice', id] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Relance impossible'),
  });

  if (invoiceQ.isLoading || suppliersQ.isLoading || refQ.isLoading) {
    return <p className="text-slate-500">Chargement…</p>;
  }
  if (invoiceQ.isError) return <p className="text-red-600">Erreur : {String(invoiceQ.error)}</p>;
  if (suppliersQ.isError) return <p className="text-red-600">Erreur fournisseurs : {String(suppliersQ.error)}</p>;
  if (refQ.isError) return <p className="text-red-600">Erreur données de référence : {String(refQ.error)}</p>;
  if (!invoiceQ.data || !suppliersQ.data || !refQ.data) return null;

  const inv = invoiceQ.data.invoice;
  const suppliers = suppliersQ.data.suppliers;
  const ref = refQ.data;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3 flex-wrap">
          <Link to={backToList} className="text-sm text-blue-600 hover:underline">
            ← Toutes les factures
          </Link>
          <span className="text-slate-300">·</span>
          <h1 className="text-xl font-semibold">{inv.filename}</h1>
          <StatusBadge status={inv.status} />
          {inv.source && (
            <span className="text-xs text-slate-500">
              Source : {inv.source}
              {inv.source_reference ? ` · ${inv.source_reference}` : ''}
            </span>
          )}
        </div>

        {/* Une facture déjà créée dans Zoho ne se relit pas : le serveur refuse,
            autant ne pas proposer le bouton. */}
        {inv.status !== 'created' && inv.status !== 'pending' && (
          <div className="flex items-center gap-2">
            {confirmeRelance ? (
              <>
                <span className="text-sm text-slate-600">
                  Relire le justificatif ? Ce qui est saisi et non enregistré sera perdu.
                </span>
                <button
                  type="button"
                  onClick={() => relancer.mutate()}
                  disabled={relancer.isPending}
                  className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {relancer.isPending ? 'Relance…' : 'Confirmer'}
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmeRelance(false)}
                  className="rounded-md px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100"
                >
                  Annuler
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => setConfirmeRelance(true)}
                className="rounded-md ring-1 ring-slate-300 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
                title="Faire relire le justificatif par Claude, avec les règles d’extraction à jour"
              >
                Relancer l'extraction
              </button>
            )}
          </div>
        )}
      </div>

      {inv.status === 'pending' && (
        <div className="rounded-md bg-amber-50 ring-1 ring-inset ring-amber-200 px-3 py-2 text-sm text-amber-800 flex items-center gap-2">
          <span
            aria-hidden
            className="inline-block h-3 w-3 rounded-full border-2 border-amber-700 border-t-transparent animate-spin"
          />
          <span>
            Extraction Claude en cours… le formulaire se remplira automatiquement
            dès qu'elle sera terminée (typiquement 10–20 s).
          </span>
        </div>
      )}
      {inv.status === 'error' && inv.error_message && (
        <div className="rounded-md bg-red-50 ring-1 ring-inset ring-red-200 px-3 py-2 text-sm text-red-800">
          Erreur d'extraction : {inv.error_message}
        </div>
      )}
      {inv.status === 'created' && <ArchiveStatusPanel invoice={inv} />}

      {/*
       * Layout 2 colonnes, hauteur = quasi-totalité de la viewport pour que la
       * facture soit lisible d'un coup d'œil. La colonne droite (formulaire)
       * scrolle indépendamment de la colonne PDF.
       */}
      {/*
       * Layout responsive :
       *   - lg+ : PDF à gauche, formulaire à droite (split 50/50, hauteur viewport)
       *   - mobile/tablet : PDF en haut (hauteur fixe), formulaire dessous (scroll naturel de la page)
       */}
      <div className="lg:grid lg:grid-cols-2 lg:gap-4 lg:h-[calc(100vh-180px)] lg:min-h-[600px] space-y-4 lg:space-y-0">
        {/* PDF */}
        <section className="bg-white rounded-lg border border-slate-200 overflow-hidden flex flex-col min-h-0 h-[60vh] lg:h-auto">
          <Suspense
            fallback={
              <p className="m-auto text-slate-500 text-sm">Chargement du viewer PDF…</p>
            }
          >
            <PDFViewer pdfBase64={inv.pdf_base64} filename={inv.filename} />
          </Suspense>
        </section>

        {/* Formulaire */}
        <section className="bg-white rounded-lg border border-slate-200 p-4 lg:overflow-auto min-h-0">
          {/*
           * `key` liée à extracted_at : le formulaire s'initialise via un
           * useState paresseux (one-shot au montage). Pour un upload manuel,
           * on arrive ici en 'pending' (extracted_data encore null), donc le
           * form naît vide. Quand l'extraction Claude se termine (polling 2 s),
           * extracted_at passe de null → timestamp : la key change, le composant
           * se remonte et l'initialiseur re-tourne avec les données extraites
           * (n° facture, dates, lignes/PU). Sans ça, les champs restaient vides
           * et la duplication last-bill s'appliquait sur des lignes vides.
           */}
          <ValidationForm
            key={inv.extracted_at ?? 'pending'}
            invoice={inv}
            suppliers={suppliers}
            referenceData={ref}
          />
        </section>
      </div>
    </div>
  );
}
